
/**
 * Controller for textual menu API.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.10.1
 * @copyright  Copyright (C) 2021-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2021-12-03 (1.1.0) (jhoward)
 *      - Add content monitoring functionality to make sure the user is seeing the latest content.
 *      - Break apart the data fetch method into multiple methods for more granular use.
 *
 *    2021-12-03 (1.1.1) (jhoward)
 *      - Add href attributes to all anchors.
 *      - Update .item cards to show the "favorite" button to the right of body content rather than underneath.
 *
 *    2022-01-07 (1.2.0) (jhoward)
 *      - Refactor menu section area to its own component (<menu-sections />).
 *
 *    2022-03-23 (1.3.0) (jhoward)
 *      - <textual-menu /> now includes menu.menu_content_area_top and menu.menu_content_area_bottom.
 *
 *    2022-04-21 (1.4.0) (jhoward)
 *      - Add support for menu and menu section version numbers in textual-menu.vue.
 *      - Hide horizontal scroll indicator on small UIs in textual-menu.vue when no horizontal scroll is available.
 *
 *    2022-04-21 (1.4.1) (jhoward)
 *      - <textual-menu> now waits for document.ready to gather cached data.
 *
 *    2024-06-10 (1.4.2) (jhoward)
 *      - Add descriptive titles to links in <textual-menu>.
 *
 *    2024-06-26 (1.5.0) (jhoward)
 *      - Add support for related menus in <textual-menu>.
 *      - Add <menu-switcher> to <textual-menu>.
 *      - Add descriptive title and aria-title attributes to <textual-menu>.
 *      - Export TextualMenuComponent interface in <textual-menu>.
 *      - Code improvements in <textual-menu>.
 *
 *    2024-07-10 (1.5.1) (jhoward)
 *      - Refactor "selected menu items" button to <textual-menu>.
 *
 *    2024-07-17 (1.6.0) (jhoward)
 *      - Allow HTML in item titles and prices in <textual-menu>.
 *      - Refactor TextualMenuComponent interface from <textual-menu> to menu defs file.
 *      - Add <textual-menu>::apiHostname prop to facilitate cross-hostname communication.
 *      - Add <textual-menu>::isConstantUpdate prop to facilitate loading data once at page load.
 *      - Add <textual-menu>::isForceLatestAssets prop to facilitate not reloading the page if assets are out of sync.
 *
 *    2024-07-18 (1.6.1) (jhoward)
 *      - Add a section subtitle output to <textual-menu>.
 *
 *    2024-08-19 (1.7.0) (jhoward)
 *      - Refactor <textual-menu> to use the new <default-section> component for textual menus.
 *      - Recalculate section offsets on window.load in <textual-menu>.
 *      - Add support for menu/section/item scheduling in <textual-menu>.
 *
 *    2024-08-26 (1.7.1) (jhoward)
 *      - Use store for selected items in <textual-menu>.
 *      - Fix scroll-to-section logic in <textual-menu>.
 *      - Refactor CSS to specific components from <textual-menu>.
 *
 *    2024-09-10 (1.7.2) (jhoward)
 *      - Fix small screen UI layout in <textual-menu>.
 *
 *    2024-10-23 (1.7.3) (jhoward)
 *      - Recalculate page link visibility after loading a new menu in <textual-menu>::switchToRelatedMenu().
 *
 *    2024-11-26 (1.7.4) (jhoward)
 *      - Load Google Material Icons from a local source in <textual-menu>.
 *
 *    2024-12-03 (1.7.5) (jhoward)
 *      - Add isCartEnabled data property and set it to false in <textual-menu>.
 *      - Only load the cart icon in <textual-menu> if isCartEnabled is true.
 *      - Only load the cart dialog in <textual-menu> if isCartEnabled is true.
 *      - Pass isCartEnabled data property to <search-menu> in <textual-menu>.
 *      - Pass isCartEnabled data property to <default-section> in <textual-menu>.
 *      - Horizontally scroll menu items into view if needed in <textual-menu>::scrollToMenuItem().
 *
 *    2024-12-03 (1.7.5) (jhoward)
 *      - Undo loading Google Material Icons from local source in <textual-menu>.
 *
 *    2025-02-12 (1.8.0) (jhoward)
 *      - Add <menu-schedules> in <textual-menu>.
 *      - Add `deviceRandomId` data property in <textual-menu>.
 *      - Set isAdminView in Vuex store in <textual-menu> mounted callback.
 *      - Read, generate if needed, and set a unique device id in <textual-menu> mounted callback.
 *      - Support textual_schedules in <textual-menu>::updateMenuData().
 *      - Support textual_schedules in <textual-menu>::normalizeMenuSchedules().
 *
 *    2025-04-08 (1.9.0) (jhoward)
 *      - Accessibility improvements in <textual-menu>.
 *      - Refactor classes and layout to not use page link global styles in <textual-menu>.
 *      - Use <section-dispatch> to determine which section component(s) to load in <textual-menu>.
 *      - Use <menu-section-list-v2> rather than v1 in <textual-menu>.
 *      - Disable the "favorites" dialog in <textual-menu>.
 *      - Remove "initialSection" data property in <textual-menu>.
 *      - No longer watching "currentSection" to scroll to section in <textual-menu> (this is handled in the <menu-section-list-v2> component).
 *      - Add non-textual class names in <textual-menu>.
 *      - Remove <textual-menu>::menuItems computed property.
 *      - Remove <textual-menu>::favoriteMenuItems computed property.
 *      - Add support for "grouped_items" returned for menu data in <textual-menu>.
 *      - Improve scrolling to sections and items in <textual-menu>.
 *
 *    2025-05-09 (1.10.0) (jhoward)
 *      - Do not render <menu-section-list-v2> when there are no sections set to display in the nav in <textual-menu>.
 *
 *    2025-09-05 (1.10.1) (jhoward)
 *      - Fix <menu-switcher> covering menu titles in <textual-menu>>
 */

import {fetchMenuData} from '../../menu-functions';
import SearchMenu from './search-menu.vue';
import { MenuSection, SearchMenuComponent, SearchMenuEmitViewMenuItemPayload } from "./defs";
import MenuSectionListV2 from "./menu-section-list-v2.vue";
import MenuSwitcher from "./menu-switcher.vue";
import {
  Menu,
  SectionOffsetStructure, TextualMenuComponent,
  UpdateMenuResponse
} from "./defs";
import {ComponentPublicInstance, defineComponent} from 'vue';
import {computedIsScheduled, methodGetNormalizedSchedules, methodIsModelScheduled} from "../../helpers/menu-sections";
import generateUuid from "../../helpers/generateUuid";
import MenuSchedules from "./schedule/MenuSchedules.vue";
import SectionDispatch from "./section/SectionDispatch.vue";
import MenuVersion from "./menu-version.vue";

export default defineComponent({
  name: "textual-menu",
  components: {
    MenuVersion,
    SectionDispatch,
    MenuSchedules,
    MenuSwitcher,
    SearchMenu,
    MenuSectionListV2,
  },
  props: {
    menuId: {
      type: Number,
      default: 0
    },
    initialSection: {
      type: Number,
      default: -1
    },
    type: {
      type: String,
      default: 'TEXTUAL'
    },
    isAdminView: {
      type: Boolean,
      default: false
    },
    apiHostname: {
      type: String,
      required: true
    },
    isConstantUpdate: {
      type: Boolean,
      default: false
    },
    isForceLatestAssets: {
      type: Boolean,
      default: false
    },
    isCartEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      menu: null as Menu | null,
      relatedMenus: [] as Menu[],
      menuScrollPositions: {} as { [key: number]: number },
      deviceAssetCode: '',
      isDev: !!window.pepHelperFunctions.getParameterByName('test').length,
      isLoading: false,
      pageLinkObserver: null as IntersectionObserver | null,
      disableNavButtonScrolling: false,
      disableNavButtonScrollingTimer: -1,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      switcherHeight: this.setSwitcherHeight(),
      showSelectedMenuItems: false,
      abortController: new AbortController(),
      abortSignal: null,

      lastContentCheck: new Date(),
      contentCheckTimeout: 60000, //1 minute
      contentCheckTimer: -1,
      contentCheckDuration: 900000, //15 minutes
      contentHash: '',

      searchIndex: '',
      searchKey: '',
      searchHost: '',

      isUserAdmin: false,

      isDocumentLoaded: 'complete' === document.readyState,

      highlightTimeout: 7500,

      deviceRandomId: '',
    };
  },
  mounted() {
    this.$store.commit('setIsAdminView', this.isAdminView);

    const randomDeviceId = localStorage.getItem('random-device-id');
    if (randomDeviceId) {
      this.deviceRandomId = randomDeviceId;
    } else {
      this.deviceRandomId = generateUuid(24);
      localStorage.setItem('random-device-id', this.deviceRandomId);
    }

    this.initializeMenuData();
    $(this.getCachedData);

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;

      this.setSwitcherHeight();

    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
      this.isDocumentLoaded = true;

      this.setSwitcherHeight();

    }, {passive: true});

    //setup datetime interval
    this.$store.commit('updateDateTimer', window.setInterval(() => {
      this.$store.commit('updateDateTime', new Date());
    }, this.$store.state.dateTimeout));

    // if (window.innerWidth < 600) {
    //   const viewportNode = document.querySelector('meta[name="viewport"]');
    //   if (viewportNode) {
    //     const currentContent = viewportNode.getAttribute('content');//width=device-width,initial-scale=1.0,maximum-scale=8.0
    //     if (currentContent) {
    //       viewportNode.setAttribute('content', currentContent.replace(/(^|\s|,)width=device-width(,|\s|$)/, '$1width=600$2'));
    //     }
    //   }
    // }
  },
  watch: {
    menu() {
      if (this.menu) {
        (this.$el as HTMLDivElement).focus();
      }
    },
  },
  computed: {
    isScheduled: computedIsScheduled,

    sectionsForNav(): MenuSection[] {
      if (this.menu) {
        return Array.from(this.menu.sections.filter((section: MenuSection) => {
          return 1 === section.ms_show_in_nav;
        }));
      }

      return [];
    },

    containerStyles() {
      return {
        '--switcher-height': this.switcherHeight + 'px'
      };
    },

    containerClasses() {
      let output: { [key: string]: boolean } = {
        'textual_menu--container-landscape': this.windowWidth > this.windowHeight,
        'menu--container-landscape': this.windowWidth > this.windowHeight,
        'textual_menu--container-portrait': this.windowWidth <= this.windowHeight,
        'menu--container-portrait': this.windowWidth <= this.windowHeight,
        'textual_menu--container-scheduled': !!(this?.menu?.normalizedSchedules || []).length && this.isScheduled,
        'menu--container-scheduled': !!(this?.menu?.normalizedSchedules || []).length && this.isScheduled,
        'textual_menu--container-not_scheduled': !this.isScheduled,
        'menu--container-not_scheduled': !this.isScheduled
      };
      if (this.menu && this.menu.page && this.menu.page.page_h1) {
        //add class for menu page
        output['textual_menu--container-' + this.menu.page.page_h1.toLowerCase().replace(/\s/g, '_').replace(/[^0-9a-z_]/gi, '')] = true;
        output['menu--container-' + this.menu.page.page_h1.toLowerCase().replace(/\s/g, '_').replace(/[^0-9a-z_]/gi, '')] = true;
      }
      return output;
    },

    currentMenuId(): number {
      if (this.menu?.menu_id) {
        return this.menu.menu_id;
      }

      return 0;
    },

    allRelatedMenus(): Menu[] {
      if (this.relatedMenus.length) {
        let output = [];
        if (this.menu && (this as unknown as TextualMenuComponent).isModelScheduled(this.menu?.normalizedSchedules || [])) {
          output.push(this.menu);
        }
        this.relatedMenus.forEach(menu => {
          if ((this as unknown as TextualMenuComponent).isModelScheduled(menu?.normalizedSchedules || [])) {
            output.push(menu);
          }
        });
        output.sort((a, b) => {
          return (a?.full_title || '').localeCompare(b?.full_title || '');
        });
        return output;
      }

      return [];
    },

    sectionOffsets(): SectionOffsetStructure[] {
      this.isDocumentLoaded;
      const output: SectionOffsetStructure[] = [];
      const parentOffsetTop = (this.$el as HTMLDivElement).offsetTop + (this.$el.parentElement?.offsetTop || 0);
      this.menu?.sections.forEach(section => {
        (this.$refs['section--' + section.ms_id] as ComponentPublicInstance[]).forEach(sectionComponent => {
          output.push({
            el: sectionComponent.$el as HTMLElement,
            id: section.ms_id,
            offsetTop: parentOffsetTop + (sectionComponent.$el as HTMLElement).offsetTop,
            hash: sectionComponent.$el.querySelector('.page-link-group-hash')?.id || ''
          });
        });
      });

      return output;
    },

  },
  methods: {
    setSwitcherHeight(): void {
      if (this.$refs.switcher) {
        this.switcherHeight = (this.$refs.switcher as ComponentPublicInstance).$el.clientHeight;
      } else {
        this.switcherHeight = 0;
      }
    },

    closeSearchMenu() {
      (this.$refs.search as ComponentPublicInstance | undefined)?.$el.dispatchEvent(new CustomEvent('close'))
    },

    isModelScheduled: methodIsModelScheduled,
    getNormalizedSchedules: methodGetNormalizedSchedules,

    switchToRelatedMenu(relatedMenuId: number, scrollToLastSavedPosition: boolean = true): Promise<void> {
      return new Promise((resolve) => {
        if (this.menu && relatedMenuId !== this.menu.menu_id) {
          let newMainMenu = null;
          let newRelatedMenus: Menu[] = [];

          this.isLoading = true;

          this.relatedMenus.forEach(relatedMenu => {
            if (relatedMenuId === relatedMenu.menu_id) {
              newMainMenu = relatedMenu;
              if (this.menu) {
                newRelatedMenus.push(this.menu);
              }
            } else {
              newRelatedMenus.push(relatedMenu);
            }
          });

          this.relatedMenus = newRelatedMenus;
          this.menu = newMainMenu;
          (this.$refs.search as SearchMenuComponent | null)?.reset();

          window.setTimeout(() => {
            //scroll to last saved position
            this.isLoading = false;
            if (scrollToLastSavedPosition) {
              window.scrollTo(0, this.getMenuLastScrollPosition());
            }

            window.setTimeout(() => {
              window.resetPageLinks();
              window.recalcPageLinks(true);

              resolve();
            }, 250);
          }, 500);
        } else {
          resolve();
        }
      });
    },

    getMenuLastScrollPosition() {
      if (this.menu && this.menuScrollPositions.hasOwnProperty(this.menu.menu_id)) {
        return this.menuScrollPositions[this.menu.menu_id];
      }

      return 0;
    },

    setupContentCheck() {
      clearTimeout(this.contentCheckTimer);
      this.contentCheckTimer = window.setTimeout(() => {
        if (this.lastContentCheck.getTime() < new Date().getTime() - this.contentCheckDuration) {
          //need to check for new data
          (this as unknown as  TextualMenuComponent).getMenuData(this.menuId, this.contentHash, 'TEXTUAL', this.isDev, this.isForceLatestAssets)
            .then(this.updateMenuData)
            .then((contentUpdated) => {
              if (contentUpdated) {
                window.pepNotify.show(10000, 'Menu Updated');
              }
            })
            .finally(() => {
              //do it all over again
              this.setupContentCheck();
            });
        } else {
          this.setupContentCheck();
        }
      }, this.contentCheckTimeout);
    },

    getCachedData() {
      //look for cached selected items for this menu
      if (this.menu) {
        const cachedSelectedMenuItems = window.pepHelperFunctions.getCachedData('textual_menu--selectedMenuItems-' + this.menu.menu_id, 1000 * 60 * 60 * 24 * 90, false);
        if (cachedSelectedMenuItems) {
          if ('object' === typeof cachedSelectedMenuItems && cachedSelectedMenuItems.DATA && 'object' === typeof cachedSelectedMenuItems.DATA) {
            this.$store.commit('setSelectedMenuItemIds', cachedSelectedMenuItems.DATA);
          }
        }
      }
    },

    renderPageLinks(entries: IntersectionObserverEntry[]) {
      let visibleCount = 0;
      const localScrollReady = document.querySelector('html')?.classList.contains('scroll-ready');

      entries.forEach(function (entry) {
        const targetNode = entry.target as HTMLElement;
        if (targetNode.dataset.scrolled === 'YES') return;

        if (entry.intersectionRatio > 0.24 || (!localScrollReady && entry.intersectionRatio > 0)) {

          targetNode.classList.add('scrolled');
          targetNode.classList.add('delay-' + visibleCount++);

          targetNode.dataset.scrolled = 'YES';
        }

      });
    },

    calculateRootMarginTop() {
      return 0;//-1 * ((this.$el.querySelector('.menu--section_nav')?.clientHeight || 0));
    },

    setupIntersectionObservers() {
      if (window.IntersectionObserver) {
        //only need to set up the observer once
        if (!this.pageLinkObserver) {
          this.pageLinkObserver = new IntersectionObserver(this.renderPageLinks, {
            root: null,	//defaults to viewport
            rootMargin: this.calculateRootMarginTop() + 'px 0px 0px 0px',
            threshold: [0, 0.1, 0.25]
          });
        }

        this.$el.querySelectorAll('.textual_menu--section .item').forEach(el => {
          this.pageLinkObserver?.observe(el);
        });
      }
    },

    updateMenuData(json: UpdateMenuResponse | null) {
      return new Promise(resolve => {
        if (json && json.STATUS === 'SUCCESS' && json.DATA.CONTENT_HASH) {

          //always update search metadata
          if (json.SETTINGS) {
            if (json.SETTINGS.SEARCH) {
              if (json.SETTINGS.SEARCH.INDEX) {
                this.searchIndex = json.SETTINGS.SEARCH.INDEX;
              }
              if (json.SETTINGS.SEARCH.KEY) {
                this.searchKey = json.SETTINGS.SEARCH.KEY;
              }
              if (json.SETTINGS.SEARCH.HOST) {
                this.searchHost = json.SETTINGS.SEARCH.HOST;
              }
            }
            if (json.SETTINGS.CONTENT_CHECK) {
              if (json.SETTINGS.CONTENT_CHECK.TIMEOUT) {
                this.contentCheckTimeout = json.SETTINGS.CONTENT_CHECK.TIMEOUT;
              }
              if (json.SETTINGS.CONTENT_CHECK.DURATION) {
                this.contentCheckDuration = json.SETTINGS.CONTENT_CHECK.DURATION;
              }
            }
            if (json.SETTINGS.USER_IS_ADMIN) {
              this.isUserAdmin = json.SETTINGS.USER_IS_ADMIN;
            }
          }

          if (this.contentHash !== json.DATA.CONTENT_HASH) {
            this.contentHash = json.DATA.CONTENT_HASH;

            if (json.DATA.MENU) {
              this.menu = this.normalizeMenuSchedules(json.DATA.MENU, (this.$store.state.isAdminView) ? 'schedules' : 'textual_schedules');
            }

            if (json.DATA.RELATED_MENUS) {
              this.relatedMenus = json.DATA.RELATED_MENUS.map((relatedMenu) => {
                return this.normalizeMenuSchedules(relatedMenu, (this.$store.state.isAdminView) ? 'schedules' : 'textual_schedules');
              });
            }

            if (json.DATA.DEVICE_ASSET_CODE) {
              this.deviceAssetCode = json.DATA.DEVICE_ASSET_CODE;
            }

            requestAnimationFrame(() => {
              this.setupIntersectionObservers();
            });
            resolve(true);
          } else {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      });
    },

    normalizeMenuSchedules(menu: Menu, scheduleType: 'schedules' | 'textual_schedules' | 'visual_schedules'): Menu {
      menu.normalizedSchedules = this.getNormalizedSchedules(menu[scheduleType] || []);

      menu.sections.map(section => {
        section.normalizedSchedules = this.getNormalizedSchedules(section[scheduleType] || []);

        if (section.grouped_items) {
          for (let groupTitle in section.grouped_items) {
            for (let subgroupTitle in section.grouped_items[groupTitle]) {
              section.grouped_items[groupTitle][subgroupTitle].forEach(item => {
                item.normalizedSchedules = this.getNormalizedSchedules(item[scheduleType] || []);
              });
            }
          }
        }

        return section;
      });

      return menu;
    },

    initializeMenuData() {
      this.isLoading = true;

      (this as unknown as  TextualMenuComponent).getMenuData(this.menuId, this.contentHash, 'TEXTUAL', this.isDev, this.isForceLatestAssets)
        .then(this.updateMenuData)
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.getCachedData();
          this.isLoading = false;
          if (this.isConstantUpdate) {
            this.setupContentCheck();
          }

          // if (this.sectionOffsets.length) {
          //   this.scrollToSection(this.sectionOffsets[0], false);
          // }
          window.scrollTo({
            behavior: 'smooth',
            top: 0
          });
        });
    },

    getMenuData: fetchMenuData,

    scrollToSection(sectionOffset: SectionOffsetStructure, isSmooth = true) {
      if (this.disableNavButtonScrolling) {
        return;
      }

      this.disableNavButtonScrolling = true;
      clearTimeout(this.disableNavButtonScrollingTimer);
      this.disableNavButtonScrollingTimer = window.setTimeout(() => {
        this.disableNavButtonScrolling = false;
      }, 1000);

      if (sectionOffset.el) {
        window.scrollTo({
          behavior: (isSmooth) ? 'smooth' : 'auto',
          top: this.getSectionTopBoundingClientRect(sectionOffset) + window.scrollY - 32 - this.getScrollTopBuffer()
        });

        // window.setTimeout(() => {
        //   (sectionOffset.el?.querySelector('h2, h3, h4') as HTMLElement | null)?.focus();
        // }, 500);
        // this.currentSection = sectionOffset.id;
      }
    },

    getSectionTopBoundingClientRect(sectionOffset: SectionOffsetStructure): number {
      if (sectionOffset.el) {
        const firstHeading: HTMLHeadingElement | null = sectionOffset.el.querySelector('h2, h3, h4');
        if (firstHeading) {
          return firstHeading.getBoundingClientRect().top;
        }
        return sectionOffset.el.getBoundingClientRect().top;
      }

      return 0;
    },

    scrollToMenuItem(payload: SearchMenuEmitViewMenuItemPayload) {
      this.switchToRelatedMenu(payload.menu_id, false)
        .then(() => {
          setTimeout(() => {
            const menuItemNode: HTMLDivElement | null = document.querySelector('#menu--item-' + payload.mi_id);
            if (menuItemNode) {
              window.scrollTo({
                behavior: 'smooth',
                top: menuItemNode.getBoundingClientRect().top + window.scrollY - 32 - this.getScrollTopBuffer()
              });

              // window.setTimeout(() => {
              //   menuItemNode.focus();
              // }, 500);

              if (document.querySelector('html')?.classList.contains('ui-mobile')) {
                menuItemNode.closest('.viewport')?.scrollTo({behavior: 'smooth', left: menuItemNode.offsetLeft});
              }

              menuItemNode.classList.add('menu--item-search_highlighted');
              window.setTimeout(() => {
                menuItemNode.classList.remove('menu--item-search_highlighted');
              }, this.highlightTimeout);
            }
            this.showSelectedMenuItems = false;
          }, 500);
        });
    },

    getScrollTopBuffer(): number {
      let output = 0;

      const switcherElement = ((this.$refs.switcher as ComponentPublicInstance | null)?.$el as HTMLDivElement | null);
      if (switcherElement) {
        if ('function' === typeof switcherElement.querySelector) {
          output = switcherElement.clientHeight;
          output -= ((switcherElement.querySelector('.menu_switcher--icon') as HTMLSpanElement | null)?.clientHeight || 0);
        }
      }

      return output;
    },
  }
})
